// Timeline page — a dropdown over the timelines this knowledge base can draw:
// one per set of Snippets described Timeline:<Name>:<step> (saved queries run
// across the database's graphs, as dashboard cards are), and the built-in
// validity-window timeline when statements carry @validFrom/@validUntil. No
// bundled demo content: what renders here came from this store or the page
// says why nothing does.

import { useEffect, useState } from 'react';
import { TimelineView } from '@/components/TimelineView';
import { useTimeline, useTimelineList } from '@/lib/timelines/useTimeline';
import { Message, messageClass } from '@/components/ui/Message';
import { useCurrentDatabase } from '@/lib/useDatabases';

const VALIDITY_ID = 'validity';

export function TimelinePage() {
  const listQ = useTimelineList();
  const [id, setId] = useState<string | undefined>(undefined);
  // A timeline belongs to the database its Snippets were saved in, so the
  // page names the database it draws from (UAT 2026-10-26: four queries
  // returned nothing because another database was in use, and nothing said so).
  const databaseName = useCurrentDatabase().database?.name ?? null;

  // The list follows the database in use: a timeline the new one does not
  // hold gives way to its first.
  useEffect(() => {
    const list = listQ.data;
    if (!list) return;
    if (!id || !list.some((t) => t.id === id)) setId(list[0]?.id);
  }, [id, listQ.data]);

  const specQ = useTimeline(id);
  const spec = specQ.data;
  const empty = listQ.data !== undefined && listQ.data.length === 0;
  const fromSnippets = id !== undefined && id !== VALIDITY_ID;

  return (
    <div className="container mx-auto max-w-[1480px] py-6">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">Timeline</h1>
          <p className="text-sm text-tres-text-secondary">
            Drawn live from {databaseName ?? 'this database'}, from saved queries or from statements
            with validity windows.
          </p>
        </div>
        {(listQ.data?.length ?? 0) > 0 && (
          <label className="flex items-center gap-2 text-sm text-tres-text-secondary">
            <span>Timeline</span>
            <select
              value={id ?? ''}
              onChange={(e) => setId(e.target.value)}
              className="rounded-md border border-input bg-card px-2 py-1.5 text-sm text-tres-text-primary"
            >
              {(listQ.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {empty && (
        <div className="rounded-lg border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
          <p className="font-medium text-tres-text-primary">
            No timelines in {databaseName ?? 'this database'} yet
          </p>
          <p className="mt-1">
            A timeline belongs to the database its saved queries were saved in; another database
            may hold some. A timeline is drawn from saved queries: save each query on the Query page as a
            Snippet described <span className="font-mono">Timeline:&lt;Name&gt;:&lt;step&gt;</span>,
            and name its columns for what they hold, such as member, joined and left, or story
            and position. Statements that carry validity windows (
            <span className="font-mono">@validFrom</span> /{' '}
            <span className="font-mono">@validUntil</span>) are drawn as a timeline of their own.
          </p>
        </div>
      )}

      {spec && (
        <section>
          <div className="mb-2">
            <h2 className="text-lg font-medium text-tres-text-primary">{spec.title}</h2>
            {fromSnippets ? (
              <div className="text-xs text-tres-text-secondary">
                <p>
                  Drawn just now{databaseName ? ` in ${databaseName}` : ''} from {spec.steps?.length ?? 0} saved{' '}
                  {(spec.steps?.length ?? 0) === 1 ? 'query' : 'queries'}, run in step order:
                </p>
                {/* The queries themselves, so a reader can see each line was
                    drawn from what the store returned (UAT 2026-10-26, 23.96:
                    "can't really tell if timelines are running from queries"). */}
                <ol className="mt-1 space-y-1 pl-4" data-testid="timeline-steps">
                  {(spec.steps ?? []).map((s) => (
                    <li key={s.step} className="list-decimal">
                      <span className="font-medium text-tres-text-primary">{s.name}</span>
                      {s.error
                        ? ` did not run: ${s.error}`
                        : `, ${s.rows} ${s.rows === 1 ? 'row' : 'rows'}`}
                      {s.query && (
                        <details className="mt-0.5">
                          <summary className="cursor-pointer text-tres-action hover:underline">Show the query</summary>
                          <pre className="mt-1 overflow-x-auto whitespace-pre rounded bg-muted px-2 py-1 font-mono text-[11px] text-tres-text-primary">
                            {s.query}
                          </pre>
                        </details>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <p className="text-xs text-tres-text-secondary">
                Each row is one statement carrying a validity window (@validFrom / @validUntil). An open left edge means
                the start is unrecorded; a span with no right edge is still in force.
              </p>
            )}
          </div>
          {(spec.notes?.length ?? 0) > 0 && (
            <Message kind="guidance" className="mb-3" data-testid="timeline-notes">
              <ul className="list-disc space-y-0.5 pl-5">
                {spec.notes!.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </Message>
          )}
          {spec.tracks.length > 0 && <TimelineView spec={spec} />}
        </section>
      )}

      {specQ.isLoading && id && (
        <div className="rounded-md border border-border bg-card p-6 text-sm text-tres-text-secondary">
          Loading…
        </div>
      )}
      {specQ.error && (
        <div className={messageClass('warning', 'normal')}>
          {(specQ.error as Error).message}
        </div>
      )}
    </div>
  );
}
